import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Link, useHistory } from 'react-router-dom';
import { useIsAuthenticated, useMsal } from '@azure/msal-react';
import RobotLogo from './RobotLogo';
import './Navbar.css';

function Navbar() {
  const { instance, accounts } = useMsal();
  const isAuthenticated = useIsAuthenticated();
  const [click, setClick] = useState(false);
  const [button, setButton] = useState(true);
  const [contentDropdown, setContentDropdown] = useState(false);
  const [toolsDropdown, setToolsDropdown] = useState(false);
  const [menuDropdown, setMenuDropdown] = useState(false);
  const menuRef = useRef(null);
  const letterRefs = useRef([]);
  const logoWrapRef = useRef(null);
  const history = useHistory();

  const LOGO_CHARS = 'coretez.io'.split('');
  const MAX_SCALE = 1.6;
  const INFLUENCE_RADIUS = 80;

  const handleLogoMouseMove = useCallback((e) => {
    const mouseX = e.clientX;
    letterRefs.current.forEach((el) => {
      if (!el) return;
      const rect = el.getBoundingClientRect();
      const letterCenterX = rect.left + rect.width / 2;
      const dist = Math.abs(mouseX - letterCenterX);
      const scale = dist < INFLUENCE_RADIUS
        ? 1 + (MAX_SCALE - 1) * Math.cos((dist / INFLUENCE_RADIUS) * (Math.PI / 2))
        : 1;
      el.style.transform = `scale(${scale})`;
    });
  }, []);

  const handleLogoMouseLeave = useCallback(() => {
    letterRefs.current.forEach((el) => {
      if (el) el.style.transform = 'scale(1)';
    });
  }, []);

  const handleClick = () => setClick(!click);
  const closeMobileMenu = () => {
    setClick(false);
    setContentDropdown(false);
    setToolsDropdown(false);
  };

  const showButton = () => {
    if (window.innerWidth <= 960) {
      setButton(false);
    } else {
      setButton(true);
    }
  };

  useEffect(() => {
    showButton();
    window.addEventListener('resize', showButton);

    const handleClickOutside = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) {
        setMenuDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);

    return () => {
      window.removeEventListener('resize', showButton);
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  return (
    <>
      <nav className='navbar'>
        <div className='navbar-container'>
          {/* Left logo (desktop only) */}
          <Link to='/' className='navbar-logo-left' onClick={closeMobileMenu}>
            <span
              className='navbar-logo-text'
              ref={logoWrapRef}
              onMouseMove={handleLogoMouseMove}
              onMouseLeave={handleLogoMouseLeave}
            >
              {LOGO_CHARS.map((char, i) => (
                <span
                  key={i}
                  className='navbar-logo-char'
                  ref={el => letterRefs.current[i] = el}
                >
                  {char === ' ' ? ' ' : char}
                </span>
              ))}
            </span>
          </Link>
          {/* Center logo */}
          <Link to='/' className='navbar-logo-center' onClick={closeMobileMenu}>
            <RobotLogo />
          </Link>
          {/* Hamburger icon (mobile only) */}
          <div className='menu-icon' onClick={handleClick}>
            <i className={click ? 'fas fa-times' : 'fas fa-bars'} />
          </div>
          {/* Nav menu (dropdown on mobile, right-aligned on desktop) */}
          <ul className={click ? 'nav-menu active' : 'nav-menu'}>
            <li className='nav-item'>
              <Link to='/' className='nav-links' onClick={closeMobileMenu}>
                Home
              </Link>
            </li>
            <li 
              className='nav-item nav-item-dropdown'
              onMouseEnter={() => button && setContentDropdown(true)}
              onMouseLeave={() => button && setContentDropdown(false)}
            >
              <Link to='/content' className='nav-links' onClick={button ? null : closeMobileMenu}>
                Blog Content <i className={`fas fa-caret-${contentDropdown || !button ? 'up' : 'down'}`}></i>
              </Link>
              {(contentDropdown || !button) && (
                <div className='mega-menu'>
                  <div className='mega-menu-grid'>
                    <div className='mega-col'>
                      <span className='mega-col-label'>Cloud</span>
                      <Link to='/content/azure' className='dropdown-link' onClick={closeMobileMenu}>
                        <i className="fas fa-cloud"></i> Azure
                      </Link>
                      <Link to='/content/ai' className='dropdown-link' onClick={closeMobileMenu}>
                        <i className="fas fa-brain"></i> AI
                      </Link>
                    </div>
                    <div className='mega-col'>
                      <span className='mega-col-label'>Microsoft</span>
                      <Link to='/content/m365' className='dropdown-link' onClick={closeMobileMenu}>
                        <i className="fab fa-microsoft"></i> M365
                      </Link>
                      <Link to='/content/general' className='dropdown-link' onClick={closeMobileMenu}>
                        <i className="fas fa-globe"></i> General
                      </Link>
                    </div>
                  </div>
                </div>
              )}
            </li>
            <li 
              className='nav-item nav-item-dropdown'
              onMouseEnter={() => button && setToolsDropdown(true)}
              onMouseLeave={() => button && setToolsDropdown(false)}
            >
              <Link to='/tools' className='nav-links' onClick={button ? null : closeMobileMenu}>
                Useful Tools <i className={`fas fa-caret-${toolsDropdown || !button ? 'up' : 'down'}`}></i>
              </Link>
              {(toolsDropdown || !button) && (
                <div className='mega-menu'>
                  <div className='mega-menu-grid'>
                    <div className='mega-col'>
                      <span className='mega-col-label'>Security</span>
                      <Link to='/tools/windows-eol' className='dropdown-link' onClick={closeMobileMenu}>
                        <i className="fab fa-windows"></i> Windows EOL
                      </Link>
                      <Link to='/tools/ms-security-cves' className='dropdown-link' onClick={closeMobileMenu}>
                        <i className="fas fa-shield-alt"></i> MS Security CVEs
                      </Link>
                    </div>
                    <div className='mega-col'>
                      <span className='mega-col-label'>Azure</span>
                      <Link to='/tools/azure-eol' className='dropdown-link' onClick={closeMobileMenu}>
                        <i className="fas fa-cloud"></i> Azure Updates/EOL
                      </Link>
                      <Link to='/tools/azure-compute-optimiser' className='dropdown-link' onClick={closeMobileMenu}>
                        <i className="fas fa-microchip"></i> Azure Compute Optimiser
                      </Link>
                    </div>
                  </div>
                </div>
              )}
            </li>
            {/* Mobile: About Us */}
            {!button && (
              <li className='nav-item nav-item-dropdown'>
                <span className='nav-links'>
                  About Us
                </span>
                <ul className='dropdown-menu' style={{position:'static'}}>
                  <li>
                    <Link to='/about/me' className='dropdown-link' onClick={closeMobileMenu}>
                      <i className='fas fa-user-circle'></i> Me
                    </Link>
                  </li>
                  <li>
                    <Link to='/about/coming-soon' className='dropdown-link' onClick={closeMobileMenu}>
                      <i className='fas fa-rocket'></i> Coming Soon
                    </Link>
                  </li>
                </ul>
              </li>
            )}
            {/* Mobile account items */}
            {!button && isAuthenticated && (
              <>
                <li className='nav-item'>
                  <Link to='/notes' className='nav-links' onClick={closeMobileMenu}>
                    Notes
                  </Link>
                </li>
                <li className='nav-item'>
                  <Link to='/profile' className='nav-links' onClick={closeMobileMenu}>
                    My Profile
                  </Link>
                </li>
                <li className='nav-item'>
                  <span className='nav-links' style={{ cursor: 'pointer' }} onClick={() => instance.logoutRedirect({ postLogoutRedirectUri: window.location.origin })}>
                    Sign Out
                  </span>
                </li>
              </>
            )}
            {!button && !isAuthenticated && (
              <li className='nav-item'>
                <Link to='/sign-up' className='nav-links' onClick={closeMobileMenu}>
                  Login
                </Link>
              </li>
            )}
          </ul>
          {/* Desktop Menu button */}
          {button && (
            <div className='nav-menu-btn-wrap' ref={menuRef}>
              <button
                className='nav-menu-btn'
                onClick={() => setMenuDropdown(prev => !prev)}
                aria-expanded={menuDropdown}
                aria-haspopup='true'
              >
                <i className='fas fa-bars'></i> Menu
              </button>
              {menuDropdown && (
                <div className='nav-menu-dropdown'>
                  <div className='nav-menu-section'>
                    <span className='nav-menu-section-label'>Pages</span>
                    <Link to='/' className='nav-menu-dropdown-link' onClick={() => setMenuDropdown(false)}>
                      <i className='fas fa-home'></i> Home
                    </Link>
                  </div>
                  <div className='nav-menu-section'>
                    <span className='nav-menu-section-label'>Blog Content</span>
                    <Link to='/content' className='nav-menu-dropdown-link' onClick={() => setMenuDropdown(false)}>
                      <i className='fas fa-newspaper'></i> All Content
                    </Link>
                    <Link to='/content/azure' className='nav-menu-dropdown-link' onClick={() => setMenuDropdown(false)}>
                      <i className='fas fa-cloud'></i> Azure
                    </Link>
                    <Link to='/content/ai' className='nav-menu-dropdown-link' onClick={() => setMenuDropdown(false)}>
                      <i className='fas fa-brain'></i> AI
                    </Link>
                    <Link to='/content/m365' className='nav-menu-dropdown-link' onClick={() => setMenuDropdown(false)}>
                      <i className='fab fa-microsoft'></i> M365
                    </Link>
                    <Link to='/content/general' className='nav-menu-dropdown-link' onClick={() => setMenuDropdown(false)}>
                      <i className='fas fa-globe'></i> General
                    </Link>
                  </div>
                  <div className='nav-menu-section'>
                    <span className='nav-menu-section-label'>Useful Tools</span>
                    <Link to='/tools' className='nav-menu-dropdown-link' onClick={() => setMenuDropdown(false)}>
                      <i className='fas fa-tools'></i> All Tools
                    </Link>
                    <Link to='/tools/windows-eol' className='nav-menu-dropdown-link' onClick={() => setMenuDropdown(false)}>
                      <i className='fab fa-windows'></i> Windows EOL
                    </Link>
                    <Link to='/tools/ms-security-cves' className='nav-menu-dropdown-link' onClick={() => setMenuDropdown(false)}>
                      <i className='fas fa-shield-alt'></i> MS Security CVEs
                    </Link>
                    <Link to='/tools/azure-eol' className='nav-menu-dropdown-link' onClick={() => setMenuDropdown(false)}>
                      <i className='fas fa-cloud'></i> Azure Updates/EOL
                    </Link>
                    <Link to='/tools/azure-compute-optimiser' className='nav-menu-dropdown-link' onClick={() => setMenuDropdown(false)}>
                      <i className='fas fa-microchip'></i> Azure Compute Optimiser
                    </Link>
                  </div>
                  <div className='nav-menu-section'>
                    <span className='nav-menu-section-label'>About Us</span>
                    <Link to='/about/me' className='nav-menu-dropdown-link' onClick={() => setMenuDropdown(false)}>
                      <i className='fas fa-user-circle'></i> Me
                    </Link>
                    <Link to='/about/coming-soon' className='nav-menu-dropdown-link' onClick={() => setMenuDropdown(false)}>
                      <i className='fas fa-rocket'></i> Coming Soon
                    </Link>
                  </div>
                  <div className='nav-menu-section'>
                    <span className='nav-menu-section-label'>Account</span>
                    {isAuthenticated ? (
                      <>
                        {accounts[0]?.name && (
                          <span className='nav-menu-dropdown-link' style={{ cursor: 'default', opacity: 0.6, fontSize: '0.85em' }}>
                            <i className='fas fa-user-check'></i> {accounts[0].name}
                          </span>
                        )}
                        <Link to='/blog/create' className='nav-menu-dropdown-link' onClick={() => setMenuDropdown(false)}>
                          <i className='fas fa-plus'></i> New Blog Post
                        </Link>
                        <Link to='/notes' className='nav-menu-dropdown-link' onClick={() => setMenuDropdown(false)}>
                          <i className='fas fa-sticky-note'></i> Notes
                        </Link>
                        <Link to='/profile' className='nav-menu-dropdown-link' onClick={() => setMenuDropdown(false)}>
                          <i className='fas fa-user'></i> My Profile
                        </Link>
                        <button
                          className='nav-menu-dropdown-link'
                          style={{ background: 'none', border: 'none', cursor: 'pointer', width: '100%', textAlign: 'left', padding: 0 }}
                          onClick={() => { setMenuDropdown(false); instance.logoutRedirect({ postLogoutRedirectUri: window.location.origin }); }}
                        >
                          <i className='fas fa-sign-out-alt'></i> Sign Out
                        </button>
                      </>
                    ) : (
                      <Link
                        to='/sign-up'
                        className='nav-menu-dropdown-link'
                        onClick={() => setMenuDropdown(false)}
                      >
                        <i className='fas fa-sign-in-alt'></i> Login
                      </Link>
                    )}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </nav>
    </>
  );
}

export default Navbar;
